import React, { useEffect, useState } from "react";
import i18n from "i18next";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { LuChevronDown, LuChevronUp } from "react-icons/lu";
import "./_appModal.scss";

const joinClassNames = (...values) => values.filter(Boolean).join(" ");

const AppModal = ({
  show,
  onClose,
  title,
  children,
  footer,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  cancelDisabled = false,
  confirmDisabled = false,
  confirmTitle,
  size = "lg",
  centered = false,
  backdrop = "static",
  keyboard = false,
  closeButton = true,
  showSceneToggle = true,
  headerContent,
  renderHeader,
  headerActions,
  className = "pageModal",
  dialogClassName,
  contentClassName,
  bodyClassName,
  headerProps = {},
  bodyProps = {},
  footerProps = {},
  titleProps = {},
  maxWidth,
  style,
  ...rest
}) => {
  const isMdSize = size === "md";
  const bootstrapSize = isMdSize ? undefined : size;
  const effectiveMaxWidth =
    maxWidth !== undefined && maxWidth !== null && maxWidth !== ""
      ? maxWidth
      : isMdSize
        ? "600px"
        : null;
  const resolvedMaxWidth =
    effectiveMaxWidth === null
      ? null
      : typeof effectiveMaxWidth === "number"
        ? `${effectiveMaxWidth}px`
        : effectiveMaxWidth;
  const mergedDialogClassName = joinClassNames(
    dialogClassName,
    resolvedMaxWidth ? "appModal--constrained" : null
  );
  const mergedStyle = resolvedMaxWidth
    ? { ...(style || {}), "--app-modal-max-width": resolvedMaxWidth }
    : style;
  const [showContent, setShowContent] = useState(true);

  useEffect(() => {
    if (!show) {
      setShowContent(true);
    }
  }, [show]);

  const modalClassName = joinClassNames("pageModal", className);

  const normalizedBodyProps = {
    ...bodyProps,
    hidden: showContent ? undefined : true,
    "aria-hidden": showContent ? undefined : true,
  };

  const normalizedFooterProps = {
    ...footerProps,
    hidden: showContent ? undefined : true,
    "aria-hidden": showContent ? undefined : true,
  };

  const contentVisibilityStyle = showContent
    ? {
        display: "block",
        visibility: "visible",
      }
    : {
        display: "none",
        visibility: "hidden",
        height: 0,
        maxHeight: 0,
        overflow: "hidden",
        padding: 0,
        margin: 0,
        opacity: 0,
        pointerEvents: "none",
      };

  const renderDefaultHeader = () => {
    if (!title && !headerActions && !closeButton && !showSceneToggle) {
      return null;
    }

    const { className: headerClass, ...restHeaderProps } = headerProps;
    const { className: titleClass, ...restTitleProps } = titleProps;
    const hasActions = headerActions || closeButton || showSceneToggle;

    return (
      <div className={joinClassNames("app-modal__header", headerClass)} {...restHeaderProps}>
        <div className="app-modal__header-title-group">
          {title && (
            <span className={joinClassNames("app-modal__title", titleClass)} {...restTitleProps}>
              {title}
            </span>
          )}
        </div>
        {hasActions && (
          <div className="app-modal__header-actions">
            {headerActions}
            {showSceneToggle && (
              <button
                type="button"
                className="app-modal__toggle-btn"
                title={
                  showContent
                    ? i18n.t("appModal.showScene") || "Sahneyi gör"
                    : i18n.t("appModal.showPanel") || "Paneli göster"
                }
                onClick={() => setShowContent((visible) => !visible)}
              >
                {showContent ? <LuChevronDown size={16} /> : <LuChevronUp size={16} />}
              </button>
            )}
            {closeButton && (
              <button
                type="button"
                className="app-modal__close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const headerNode = (() => {
    if (typeof renderHeader === "function") {
      return renderHeader({ close: onClose });
    }
    if (headerContent) {
      return headerContent;
    }
    return renderDefaultHeader();
  })();

  const standardFooter =
    cancelLabel || confirmLabel ? (
      <div className="app-modal__footer">
        {cancelLabel ? (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={onCancel || onClose}
            disabled={cancelDisabled}
          >
            {cancelLabel}
          </button>
        ) : null}
        {confirmLabel ? (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            onClick={onConfirm}
            disabled={confirmDisabled}
            title={confirmTitle}
          >
            {confirmLabel}
          </button>
        ) : null}
      </div>
    ) : null;

  const resolvedFooter = footer !== undefined ? footer : standardFooter;

  return (
    <Modal
      show={show}
      onHide={onClose}
      size={bootstrapSize}
      centered={centered}
      backdrop={backdrop}
      keyboard={keyboard}
      className={modalClassName}
      dialogClassName={mergedDialogClassName}
      contentClassName={contentClassName}
      style={mergedStyle}
      {...rest}
    >
      {headerNode}
      <div
        style={contentVisibilityStyle}
        aria-hidden={showContent ? undefined : true}
        hidden={showContent ? undefined : true}
      >
        <Modal.Body className={bodyClassName} {...normalizedBodyProps}>
          {children}
        </Modal.Body>
      </div>
      {resolvedFooter ? (
        <div
          style={contentVisibilityStyle}
          aria-hidden={showContent ? undefined : true}
          hidden={showContent ? undefined : true}
        >
          <Modal.Footer {...normalizedFooterProps}>{resolvedFooter}</Modal.Footer>
        </div>
      ) : null}
    </Modal>
  );
};

AppModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  children: PropTypes.node,
  footer: PropTypes.node,
  cancelLabel: PropTypes.node,
  confirmLabel: PropTypes.node,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  cancelDisabled: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  confirmTitle: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  centered: PropTypes.bool,
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(["static"])]),
  keyboard: PropTypes.bool,
  closeButton: PropTypes.bool,
  showSceneToggle: PropTypes.bool,
  headerContent: PropTypes.node,
  renderHeader: PropTypes.func,
  headerActions: PropTypes.node,
  className: PropTypes.string,
  dialogClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  headerProps: PropTypes.object,
  bodyProps: PropTypes.object,
  footerProps: PropTypes.object,
  titleProps: PropTypes.object,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  style: PropTypes.object,
};

export default AppModal;
